import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { Link, createFileRoute } from "@tanstack/react-router";
import { cn } from "@pontimed/ui";
import { m } from "framer-motion";
import { Calendar, Clock, MapPin, Stethoscope } from "lucide-react";
import { useEffect, useState } from "react";

import { AddToCalendarButton } from "@/components/booking/add-to-calendar-button";
import { DrawnCheck } from "@/components/booking/drawn-check";
import { PAGE_TITLE_CLASS } from "@/components/layout/page-header";
import { buttonVariants } from "@/components/ui/button-variants";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppointmentTypeLabel } from "@/lib/appointment-labels";

interface ProviderSummary {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  primaryCountry: string | null;
  consultationTypes: string[] | null;
  specialty: { name: Record<string, string> | string } | null;
}

interface FacilitySummary {
  id: string;
  name: Record<string, string> | string | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressFormatted: string | null;
}

interface AppointmentDetail {
  id: string;
  startsAt: string;
  endsAt: string;
  duration: number;
  type: string;
  status: string;
  consultationName: string | null;
  price: string | number | null;
  currency: string | null;
  provider: ProviderSummary;
  facility: FacilitySummary | null;
}

export const Route = createFileRoute("/_authenticated/account/booking/confirmation/$appointmentId")(
  {
    component: BookingConfirmationPage,
  },
);

function BookingConfirmationPage() {
  const { appointmentId } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  const [appointment, setAppointment] = useState<AppointmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiFetch
      .get<AppointmentDetail>(`/api/appointments/${appointmentId}`, { signal: controller.signal })
      .then((data) => setAppointment(data))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiError) {
          if (err.status === 404) setError(t`Appointment not found`);
          else if (err.status === 403) setError(t`You don't have access to this appointment`);
          else setError(t`Failed to load appointment`);
        } else {
          // Never show raw error text (HTTP codes, English messages) to patients.
          setError(t`Failed to load appointment`);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [appointmentId, t]);

  if (loading) return <ConfirmationSkeleton />;
  if (error) return <ConfirmationError message={error} />;
  if (!appointment) return null;

  return <ConfirmationView appointment={appointment} locale={locale} />;
}

function ConfirmationView({
  appointment,
  locale,
}: {
  appointment: AppointmentDetail;
  locale: string;
}) {
  const { t } = useLingui();
  const appointmentTypeLabel = useAppointmentTypeLabel();
  const startDate = new Date(appointment.startsAt);
  const dateFormatted = startDate.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const timeFormatted = startDate.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const providerName = `${appointment.provider.firstName} ${appointment.provider.lastName}`;
  const specialtyName = pickLocalized(appointment.provider.specialty?.name ?? null, locale);
  const facilityName = pickLocalized(appointment.facility?.name ?? null, locale);
  const facilityAddress = appointment.facility
    ? appointment.facility.addressFormatted ||
      [appointment.facility.addressStreet, appointment.facility.addressCity]
        .filter(Boolean)
        .join(", ")
    : null;

  const priceValue =
    appointment.price != null
      ? typeof appointment.price === "string"
        ? parseFloat(appointment.price)
        : appointment.price
      : null;

  const priceFormatted =
    priceValue != null && appointment.currency
      ? new Intl.NumberFormat(locale, {
          style: "currency",
          currency: appointment.currency,
          minimumFractionDigits: 0,
        }).format(priceValue)
      : null;

  const displayType =
    appointment.consultationName ||
    typeLabel(appointment.type, t) ||
    appointmentTypeLabel(appointment.type);

  return (
    <div className="flex w-full justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md">
        {/* Header — the booking sheet already celebrated with "Appointment
            confirmed"; this page is the details view it links to (P26), so a
            small check and a plain title, not a second celebration. */}
        <div className="flex flex-col items-center text-center">
          <DrawnCheck tone="success" className="mb-4 size-12" />
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <h1 className={PAGE_TITLE_CLASS}>
              <Trans>Appointment details</Trans>
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              <Trans>You'll receive reminders by email and push.</Trans>
            </p>
          </m.div>
        </div>

        {/* Summary */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border-border mt-8 rounded-xl border p-5"
        >
          <h2 className="text-foreground text-sm font-semibold">
            <Trans>Appointment summary</Trans>
          </h2>

          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3">
              {appointment.provider.photoUrl ? (
                <img
                  src={appointment.provider.photoUrl}
                  alt={providerName}
                  className="size-10 rounded-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
                  <Stethoscope className="text-primary size-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-medium">{providerName}</p>
                {specialtyName && <p className="text-muted-foreground text-xs">{specialtyName}</p>}
              </div>
            </div>

            <div className="border-border border-t" />

            <div className="flex items-start gap-3">
              <Calendar className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
              <div>
                <p className="text-foreground text-sm capitalize">{dateFormatted}</p>
                <p className="text-muted-foreground text-xs">
                  {timeFormatted} · {appointment.duration} <Trans>min</Trans>
                </p>
              </div>
            </div>

            {facilityName && (
              <div className="flex items-start gap-3">
                <MapPin className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
                <div>
                  <p className="text-foreground text-sm">{facilityName}</p>
                  {facilityAddress && (
                    <p className="text-muted-foreground text-xs">{facilityAddress}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Clock className="text-muted-foreground size-4 shrink-0" aria-hidden />
              <span className="bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-medium">
                {displayType}
              </span>
            </div>

            {priceFormatted && (
              <>
                <div className="border-border border-t" />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    <Trans>Total</Trans>
                  </span>
                  <span className="text-foreground text-sm font-semibold">{priceFormatted}</span>
                </div>
              </>
            )}
          </div>
        </m.div>

        {/* Add to calendar */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="mt-6 flex justify-center"
        >
          <AddToCalendarButton
            appointmentId={appointment.id}
            providerName={providerName}
            startsAt={appointment.startsAt}
            endsAt={appointment.endsAt}
            location={[facilityName, facilityAddress].filter(Boolean).join(", ") || undefined}
            consultationType={displayType}
          />
        </m.div>

        {/* Action buttons */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-8 space-y-3"
        >
          {/* Links styled as buttons — never a <button> inside an <a> (P26). */}
          <Link
            to="/account/user/appointments"
            search={{ tab: "upcoming" }}
            className={cn(
              buttonVariants({ variant: "outline" }),
              "h-12 w-full text-sm font-medium",
            )}
          >
            <Trans>View my appointments</Trans>
          </Link>
          <Link
            to="/account/user/dashboard"
            className={cn(buttonVariants(), "h-12 w-full text-sm font-semibold")}
          >
            <Trans>Done</Trans>
          </Link>
        </m.div>
      </div>
    </div>
  );
}

function ConfirmationSkeleton() {
  return (
    <div className="flex w-full justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="size-12 rounded-full" />
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
      </div>
    </div>
  );
}

function ConfirmationError({ message }: { message: string }) {
  return (
    <div className="flex w-full justify-center px-4 py-10">
      <div className="border-destructive/20 bg-card flex w-full max-w-md flex-col gap-3 rounded-xl border p-6 text-center">
        <h1 className="text-foreground text-base font-semibold">
          <Trans>Couldn't load appointment</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">{message}</p>
        <Link
          to="/account/user/dashboard"
          className={cn(buttonVariants({ variant: "outline", size: "touch" }), "w-full")}
        >
          <Trans>Back to dashboard</Trans>
        </Link>
      </div>
    </div>
  );
}

function pickLocalized(
  value: Record<string, string> | string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as Record<string, string>;
      return parsed[locale] ?? parsed.ro ?? parsed.en ?? null;
    } catch {
      return value;
    }
  }
  return value[locale] ?? value.ro ?? value.en ?? null;
}

/** Labels specific to this page; anything else goes through the shared
 *  appointment-type labels (never the raw enum). */
function typeLabel(type: string, t: ReturnType<typeof useLingui>["t"]): string | null {
  if (type === "in_office" || type === "in_person") return t(msg`In-person consultation`);
  if (type === "video") return t(msg`Video consultation`);
  if (type === "home_visit") return t(msg`Home visit`);
  if (type === "photo") return t(msg`Photo consultation`);
  return null;
}
